import * as angular from 'angular';
import * as _ from 'lodash';
import * as moment from 'moment';
import {States} from "../../shared/config/constant.module";
import template from './criticalDefectReport.tpl.html';
import {Plant} from "../../models/navigation";
import {apsViewModels} from "../../shared/dto/APS.ViewModels";
import {StateParams} from "../../common";
import LoaderServiceName, {LoaderService} from "../../components/loader/loader.service";
import ReportServiceName, {ReportService} from "../../services/reports/report.service";
import DateUtilityService from "../../services/utility/dateutility.service";
import NavigationServiceName, {NavigationService} from "../../services/navigation/navigation.service";
import { ICommonQueryingReport } from './commonQueryingReport';

let requires = [LoaderServiceName, ReportServiceName, NavigationServiceName];

interface CriticalDefectReportController extends ICommonQueryingReport {
  results: apsViewModels.CriticalDefectReportDetail[];
  searchPerformed: boolean;

  filteredResults(): apsViewModels.CriticalDefectReportDetail[];
  generateReport(): void;
  formatTime(time: Date): string;
  exportToCsv(): any[];
  toggleMissingSignOff(detail: any): void;
  formatMissingSignOff(detail: apsViewModels.CriticalDefectReportDetail): string;
}

export interface CriticalDefectReportParams extends StateParams {
  reportPlantId: string;
  reportShopId: string;
  startDate: string;
  endDate: string;
  autoRun: boolean;
}

class CriticalDefectReportCtrl implements CriticalDefectReportController {
  plants: Plant[];
  selectedPlant: Plant;
  isMissingSignOff: boolean;
  startDate: Date;
  endDate: Date;
  form: angular.IFormController;
  selectedShop: apsViewModels.ShopNavigation;
  requireShopSelection: boolean;
  uiStartDate: Date;
  uiEndDate: Date;
  requirePlantSelection: boolean;

  private $stateParams: CriticalDefectReportParams;
  private $state: angular.ui.IStateService;

  results: apsViewModels.CriticalDefectReportDetail[];
  searchPerformed: boolean;

  loaderService: LoaderService;
  reportService: ReportService;
  private $q: angular.IQService;

  shifts: string[];
  selectedShift?: string;
  items: string[];
  selectedItem?: string;
  defects: string[];
  selectedDefect?: string;
  sections: string[];
  selectedSection?: string;
  locations: string[];
  selectedLocation?: string;
  molds: string[];
  selectedMold?: string;

  constructor(
    $stateParams: CriticalDefectReportParams,
    $state: angular.ui.IStateService,
    $q: angular.IQService,
    navigationService: NavigationService,
    reportService: ReportService,
    loaderService: LoaderService
  ) {
    this.$q = $q;
    this.$state = $state;
    this.$stateParams = $stateParams;
    this.loaderService = loaderService;
    this.reportService = reportService;

    this.requirePlantSelection = true;

    var today = moment().startOf('day');
    this.startDate = today.clone().subtract(1, 'month').toDate();
    this.endDate = today.toDate();

    var promise = navigationService.getNavigation().then((navModel) => {
      this.plants = navModel.plants();
      this.selectedPlant = navModel.selectedPlant;
      this.updatePlant();
      this.selectedShop = navModel.selectedShop;

      return this.loadState($stateParams);
    });

    loaderService.loadingPromise(promise);
  }

  filteredResults(): apsViewModels.CriticalDefectReportDetail[] {
    var results = _(this.results);
    if (this.selectedShift) {
      results = results.filter(x => x.shiftLetter === this.selectedShift);
    }
    if (this.selectedItem) {
      results = results.filter(x => x.item === this.selectedItem);
    }
    if (this.selectedDefect) {
      results = results.filter(x => x.defect === this.selectedDefect);
    }
    if (this.selectedSection) {
      results = results.filter(x => x.sectionNumber === this.selectedSection);
    }
    if (this.selectedLocation) {
      results = results.filter(x => x.location === this.selectedLocation);
    }
    if (this.selectedMold) {
      results = results.filter(x => x.moldNumber === this.selectedMold);
    }
    return results.value();
  }

  updateDropdowns(): void {
    this.shifts = _.uniq(_.filter(_.map(this.results, 'shiftLetter'), x => !!x));
    this.items = _.uniq(_.filter(_.map(this.results, 'item'), x => !!x));
    this.defects = _.uniq(_.filter(_.map(this.results, 'defect'), x => !!x));
    this.sections = _.uniq(_.filter(_.map(this.results, 'sectionNumber'), x => !!x));
    this.locations = _.uniq(_.filter(_.map(this.results, 'location'), x => !!x));
    this.molds = _.uniq(_.filter(_.map(this.results, 'moldNumber'), x => !!x));
  }

  updatePlant(): void {
    this.selectedShop = null;
  }

  toggleMissingSignOff(detail: any): void {
    detail.showMissingSignOff = !detail.showMissingSignOff;
  }

  formatMissingSignOff(detail: apsViewModels.CriticalDefectReportDetail): string {
    return _.join(detail.missingSignOffFrom, ", ");
  }

  loadState(params: CriticalDefectReportParams): angular.IPromise<any> {
    if (params.reportPlantId && params.reportPlantId !== params.plantId) {
      var plant = _.find(this.plants, {id: params.reportPlantId});
      if (plant) {
        this.selectedPlant = plant;
        let shop = _.find(plant.shops, {id: params.reportShopId});
        if (shop) {
          this.selectedPlant = plant;
          this.selectedShop = shop;
        }
      }
    }

    if (params.startDate) {
      var startDate = moment(params.startDate);
      if (startDate.isValid()) {
        this.startDate = startDate.toDate();
      }
    }

    if (params.endDate) {
      var endDate = moment(params.endDate);
      if (endDate.isValid()) {
        this.endDate = endDate.toDate();
      }
    }

    if (params.autoRun) {
      return this.runDefectReport();
    }

    return this.$q.when();
  }

  saveState() {
    var reportPlantId = this.selectedPlant ? this.selectedPlant.id : undefined;
    if (reportPlantId === this.$stateParams.plantId) {
      reportPlantId = undefined;
    }
    let reportShopId = this.selectedShop ? this.selectedShop.id : undefined;
    if (reportShopId === this.$stateParams.shopId) {
      reportShopId = undefined;
    }
    var params: CriticalDefectReportParams = {
      plantId: this.$stateParams.plantId,
      shopId: this.$stateParams.shopId,
      userId: this.$stateParams.userId,
      reportPlantId: reportPlantId,
      reportShopId: reportShopId,
      startDate: this.startDate ? this.startDate.toISOString() : undefined,
      endDate: this.endDate ? this.endDate.toISOString() : undefined,
      autoRun: true
    };

    this.$state.transitionTo(States.CRITICAL_DEFECT_REPORT, params);
  }

  generateReport() {
    if (this.form.$invalid) {
      return;
    }

    this.saveState();

    this.loaderService.loadingPromise(this.runDefectReport());
  }

  private runDefectReport(): angular.IPromise<any> {
    var startDateStart = moment(this.startDate).startOf('day').toDate();
    var tzOffsetStartDate = DateUtilityService.localToTimeZone(startDateStart, this.selectedPlant.timeZoneId).toDate();

    var endDateEnd = moment(this.endDate || new Date()).endOf('day').toDate();
    var tzOffsetEndDate = DateUtilityService.localToTimeZone(endDateEnd, this.selectedPlant.timeZoneId).toDate();

    return this.reportService.getCriticalDefectReportDetails(this.selectedPlant.id, 
      tzOffsetStartDate, tzOffsetEndDate, this.isMissingSignOff)
      .then(reportItems => {
        this.results = reportItems;
        this.searchPerformed = true;
        this.updateDropdowns();
      });
  }

  exportToCsv() {
    return _.map<apsViewModels.CriticalDefectReportDetail, any>(this.filteredResults(), (x) => {
      return {
        plant: this.selectedPlant.name,
        shop: x.shop,
        shiftLetter: x.shiftLetter,
        timeFound: this.formatTime(x.timeFound),
        item: x.item,
        defect: x.defect,
        sectionNumber: x.sectionNumber,
        location: x.location,
        moldNumber: x.moldNumber,
        originator: x.originator,
        whereFound: x.whereFound,
        rootCause: x.rootCause,
        fix: x.fix,
        timeCorrected: x.timeCorrected,
        isMissingSignOff: x.isMissingSignOff ? "Missing Sign Off" : "Sign Off Complete"
      };
    })
  }

  formatTime(time: Date): string {
    return DateUtilityService.formatTime(time, this.selectedPlant.timeZoneId);
  }
}

export default angular.module('aps.controller.criticalDefectReport', requires)
  .config(function ($stateProvider: angular.ui.IStateProvider) {
    $stateProvider.state({
      name: States.CRITICAL_DEFECT_REPORT,
      url: '/admin/reports/criticalDefects?startDate&endDate&autoRun&reportPlantId',
      params: {
        startDate: {dynamic: true},
        endDate: {dynamic: true},
        autoRun: {dynamic: true},
        reportPlantId: {dynamic: true},
      },
      controller: 'CriticalDefectReportCtrl as vm',
      template: template,
      data: {
        pageTitle: 'Critical Defect Report'
      }
    });
  })
  .controller('CriticalDefectReportCtrl', CriticalDefectReportCtrl)
  .name;


// WEBPACK FOOTER //
// ./src/app/controllers/reports/criticalDefectReport.controller.ts